import dayjs from "dayjs";

/**
 * Plate helpers — the data model behind the My Work redesign
 * (approved mock 8/31; backend: dms_backend #2063).
 *
 * Every row carries a `category`: the kind of judgment it needs, a
 * vocabulary shared with the backend. Older backends omit it, so
 * {@link categoryOf} falls back by kind — conservatively, into the
 * honest residual buckets, never a wrong specific one.
 */

export const CATEGORY_ORDER = [
  "cavc",
  "bvabrief",
  "expert",
  "decision",
  "evidence",
  "filereview",
  "handoff",
  "signature",
  "contact",
  "litifytask",
  "doc",
];

/** label = card title in "by category"; short = the row tag. */
export const CATEGORY_META = {
  cavc: { label: "CAVC", short: "CAVC", dot: "#0C53BB" },
  bvabrief: { label: "BVA Briefs", short: "BVA Brief", dot: "#444CE7" },
  expert: { label: "Expert reports", short: "Expert report", dot: "#7A5AF8" },
  decision: { label: "VA decisions", short: "VA decision", dot: "#D92D20" },
  evidence: { label: "Lay Statements", short: "Lay statement", dot: "#0F7A3E" },
  filereview: { label: "File reviews", short: "File review", dot: "#B26B00" },
  handoff: { label: "Handoffs", short: "Handoff", dot: "#EF6820" },
  signature: { label: "Signatures", short: "Signature", dot: "#088AB2" },
  contact: { label: "Client contact", short: "Call", dot: "#DD2590" },
  litifytask: { label: "Litify Tasks", short: "Litify Task", dot: "#667085" },
  doc: { label: "Documents to review", short: "Review", dot: "#0E9384" },
};

/** Older backends send retired vocabulary — translate, don't drop. */
const LEGACY_CATEGORY = {
  court: "cavc",
  task: "litifytask",
};

const KIND_FALLBACK = {
  work_item: "handoff",
  signature: "signature",
  task: "litifytask",
  doc_review: "doc",
};

export const categoryOf = (row) => {
  // Keep every legacy Litify task in one window. Title-based categories
  // would otherwise scatter task rows into unrelated windows.
  if (row?.kind === "task") return "litifytask";
  if (row?.category && CATEGORY_META[row.category]) return row.category;
  if (row?.category && LEGACY_CATEGORY[row.category]) {
    return LEGACY_CATEGORY[row.category];
  }
  return KIND_FALLBACK[row?.kind] || "doc";
};

const categoryRank = (row) => {
  const index = CATEGORY_ORDER.indexOf(categoryOf(row));
  return index === -1 ? CATEGORY_ORDER.length : index;
};

/* ------------------------------------------------------------------ *
 * Handoffs are not a category (Matthew, 9/9: "What does 'handoff'
 * mean anyway? … kind of all consuming"; "look at 'Handoffs' — not
 * super helpful"). A plain work item that isn't CAVC / BVA Brief /
 * etc. is really "Order exam" or "Get statement" on a particular
 * client. So: the row tag reads the item's own type, and in By
 * category the one Handoffs pile becomes one card per client.
 * ------------------------------------------------------------------ */

/** Doorway `typeValue` → row tag. Order = how rows sort inside a card
 * (the composer's own button order: talk to the client, then get the
 * paper, then the drafting). */
export const TASK_TYPE_ORDER = [
  "Call Client",
  "Get Statement",
  "Order Exam",
  "Request Records",
  "Draft Appeal",
  "Prepare Brief",
  "File with Court",
  "File Review",
  "Decision Review",
  "Document Review",
  "Other",
];

export const TASK_TYPE_META = {
  "Call Client": { label: "Call client", dot: "#DD2590" },
  "Get Statement": { label: "Get statement", dot: "#0F7A3E" },
  "Order Exam": { label: "Order exam", dot: "#7A5AF8" },
  "Request Records": { label: "Request records", dot: "#0E9384" },
  "Draft Appeal": { label: "Draft appeal", dot: "#444CE7" },
  "Prepare Brief": { label: "Prepare brief", dot: "#444CE7" },
  "File with Court": { label: "File with court", dot: "#0C53BB" },
  "File Review": { label: "File review", dot: "#B26B00" },
  "Decision Review": { label: "Decision review", dot: "#D92D20" },
  "Document Review": { label: "Document review", dot: "#B54708" },
  Other: { label: "Work item", dot: "#EF6820" },
};

/** The item type behind a plain-handoff row, or null for every other
 * row (specific categories keep their own tag). */
export const taskTypeOf = (row) => {
  if (row?.kind !== "work_item" || categoryOf(row) !== "handoff") return null;
  const type = row?.work_item?.typeValue;
  return type && TASK_TYPE_META[type] ? type : "Other";
};

const taskTypeRank = (row) => {
  const type = taskTypeOf(row);
  if (!type) return 0;
  const index = TASK_TYPE_ORDER.indexOf(type);
  return index === -1 ? TASK_TYPE_ORDER.length : index;
};

/** What the row's tag says: the item type for a plain handoff, the
 * category otherwise. */
export const rowTagOf = (row) => {
  const type = taskTypeOf(row);
  if (type) return TASK_TYPE_META[type];
  const meta = CATEGORY_META[categoryOf(row)];
  return { label: meta.short, dot: meta.dot };
};

/**
 * Deadline math for a row's `due_on`.
 * Returns null when there is no (valid) due date, else
 * `{ days, label, detail, urgency }` where urgency is
 * "over" (past due) / "hot" (≤2d) / "soon" (≤7d) / "calm".
 */
export const dueInfo = (dueOn, now = dayjs()) => {
  if (!dueOn) return null;
  const parsed = dayjs(dueOn);
  if (!parsed.isValid()) return null;
  const days = parsed.startOf("day").diff(dayjs(now).startOf("day"), "day");
  const label = parsed.format("ddd DD MMM");
  const detail =
    days < 0 ? `${-days}d overdue` : days === 0 ? "today" : `in ${days}d`;
  const urgency =
    days < 0 ? "over" : days <= 2 ? "hot" : days <= 7 ? "soon" : "calm";
  return { days, label, detail, urgency };
};

/** Age color: red ≥10d, amber ≥5d, else quiet (approved mock). */
export const ageTone = (ageDays) => {
  if (ageDays >= 10) return "red";
  if (ageDays >= 5) return "amber";
  return null;
};

/** Worst-first inside a card: nearest deadline, then oldest. */
export const rowOrder = (a, b, now = dayjs()) => {
  const da = dueInfo(a.due_on, now);
  const db = dueInfo(b.due_on, now);
  if (da && db) {
    return da.days - db.days || (b.age_days || 0) - (a.age_days || 0);
  }
  if (da) return -1;
  if (db) return 1;
  return (b.age_days || 0) - (a.age_days || 0);
};

/* ------------------------------------------------------------------ *
 * Activation — the snooze/blocked/outside/chain build (Matthew, 9/4).
 * Every work-item row's raw doorway payload rides along at
 * `row.work_item`; only work items carry a meaningful activation,
 * everything else (doc reviews, signatures, tasks) is always
 * "active". `home_flow.py` defaults a missing field to "active" for
 * an older doorway, but a client this far downstream defends too.
 * ------------------------------------------------------------------ */

const formatMD = (iso) => {
  const parsed = dayjs(iso);
  return parsed.isValid() ? parsed.format("M/D") : "";
};

/** A snoozed row whose `snoozedUntil` has already passed reads as
 * active — the doorway flips it back itself, but the client defends
 * the same rule so a stale poll never shows a wrong fold. */
const isSnoozeExpired = (until, now = dayjs()) => {
  if (!until) return false;
  const parsed = dayjs(until);
  return parsed.isValid() && !parsed.startOf("day").isAfter(now.startOf("day"));
};

export const activationOf = (row, now = dayjs()) => {
  if (row?.kind !== "work_item") return "active";
  const raw = row.activation || row?.work_item?.activation || "active";
  if (raw === "snoozed" && isSnoozeExpired(row?.work_item?.snoozedUntil, now)) {
    return "active";
  }
  return raw;
};

/** An "outside" row whose own staleness already reads red (Chase or
 * Overdue) must stay loud in the active list, never fold away
 * quietly (Matthew, 9/4). */
export const isLoudOutside = (row) => row.staleness === "red";

/** Split a card/window's rows into what Home actually renders:
 * `active` (includes blocked rows, greyed in place — never moved),
 * `outside` (folds into "Waiting on client / vendor" unless loud),
 * and `snoozed` (folds into "Snoozed"). Order within each bucket is
 * preserved from the input. */
export const partitionByActivation = (rows, now = dayjs()) => {
  const active = [];
  const outside = [];
  const snoozed = [];
  (rows || []).forEach((row) => {
    const activation = activationOf(row, now);
    if (activation === "outside" && !isLoudOutside(row)) {
      outside.push(row);
    } else if (activation === "snoozed") {
      snoozed.push(row);
    } else {
      active.push(row);
    }
  });
  return { active, outside, snoozed };
};

/** The one-line state a folded/greyed row shows in place of its usual
 * due/age meta. Null for plain active rows and for loud outside rows
 * (their own chip already speaks). */
export const stateLineFor = (row, now = dayjs()) => {
  const activation = activationOf(row, now);
  const workItem = row?.work_item || {};
  if (activation === "outside") {
    return isLoudOutside(row) ? null : workItem.outsideLabel || null;
  }
  if (activation === "snoozed") {
    const when = formatMD(workItem.snoozedUntil);
    const note = workItem.snoozeNote;
    return `Snoozed${when ? ` until ${when}` : ""}${note ? ` — ${note}` : ""}`;
  }
  if (activation === "blocked") {
    return `Coming up — after ${workItem.blockedBySubject || "another item"} is approved`;
  }
  return null;
};

/** "On a clock": every row due within 14 days or overdue, soonest
 * first — so court dates never hide inside a card. Snoozed/blocked
 * rows are excluded unless they're actually overdue (Matthew, 9/4) —
 * a paused item shouldn't clutter the deadline table just because it
 * still has a due date sitting out there. */
export const onAClockRows = (rows, now = dayjs()) =>
  (rows || [])
    .map((row) => ({ row, due: dueInfo(row.due_on, now) }))
    .filter((entry) => entry.due && entry.due.days <= 14)
    .filter((entry) => {
      const activation = activationOf(entry.row, now);
      if (activation === "snoozed" || activation === "blocked") {
        return entry.due.urgency === "over";
      }
      return true;
    })
    .sort((a, b) => a.due.days - b.due.days)
    .map((entry) => entry.row);

const NO_DUE = 9999;

/** One card per matter, ordered by nearest due date, then worst age.
 * Inside a card, rows sort by category first (fixed order), then
 * urgency — "organized first by client and then by the sub-type"
 * (Matthew, 8/31 redlines). */
export const groupByClient = (rows, now = dayjs()) => {
  const byMatter = new Map();
  (rows || []).forEach((row) => {
    const key = String(row.matter_id ?? row.matter_name ?? "none");
    if (!byMatter.has(key)) {
      byMatter.set(key, { key, matterName: row.matter_name || "", rows: [] });
    }
    byMatter.get(key).rows.push(row);
  });
  return [...byMatter.values()]
    .map((group) => {
      let minDue = NO_DUE;
      let maxAge = 0;
      group.rows.forEach((row) => {
        const due = dueInfo(row.due_on, now);
        if (due) minDue = Math.min(minDue, due.days);
        maxAge = Math.max(maxAge, row.age_days || 0);
      });
      return {
        ...group,
        minDue,
        maxAge,
        rows: [...group.rows].sort(
          (a, b) =>
            categoryRank(a) - categoryRank(b) ||
            taskTypeRank(a) - taskTypeRank(b) ||
            rowOrder(a, b, now)
        ),
      };
    })
    .sort((a, b) => a.minDue - b.minDue || b.maxAge - a.maxAge);
};

/** The categories that keep their own window in By category (Matthew,
 * 9/9: "I personally like my 'CAVC' and 'BVA Brief' categories but
 * most everything else can be organized by client"). */
export const CATEGORY_WINDOWS = ["cavc", "bvabrief"];

/**
 * By category: a window for CAVC and one for BVA Briefs, fixed order,
 * omitted when empty — then everything else as one card PER CLIENT
 * (Matthew, 9/9). Those cards carry `matterKey` / `matterName`, are
 * ordered like By client (nearest due, then worst age), and their rows
 * sort by category, then item type, then urgency, each tagged by what
 * it is — never a "Handoffs" pile.
 */
export const groupByCategory = (rows, now = dayjs()) => {
  const all = rows || [];
  const windows = CATEGORY_WINDOWS.flatMap((category) => {
    const matching = all.filter((row) => categoryOf(row) === category);
    if (matching.length === 0) return [];
    return [
      {
        category,
        key: category,
        rows: matching.sort((a, b) => rowOrder(a, b, now)),
      },
    ];
  });
  const rest = all.filter((row) => !CATEGORY_WINDOWS.includes(categoryOf(row)));
  const clientCards = groupByClient(rest, now).map((group) => ({
    category: "client",
    key: `client:${group.key}`,
    matterKey: group.key,
    matterName: group.matterName,
    rows: group.rows,
  }));
  return [...windows, ...clientCards];
};
